<template>
  <div class="video-container" @mousemove="onMouseMove" @mouseleave="onMouseLeave">
    <template v-if="src">
      <video
        id="video"
        ref="video"
        :controls="native"
        :src="src"
        playsinline
        controlsList="nodownload"
        preload="metadata"
        :poster="poster"
        tabindex="0"
        @contextmenu="disableContext"
        @timeupdate="updateProgress"
        @play="onPlay"
        @pause="onPause"
        @click="togglePlayPause"
      >
        <source :src="src" type="video/mp4" />
      </video>

      <template v-if="!native">
        <div class="player-state">
          <button
            ref="backwardState"
            class="state-btn state-backward"
            aria-label="5 seconden terug"
            @click="rewind"
          >
            <Icon name="eenvandaag-general:rewind" role="presentation" />
            <span class="backward-duration">5</span>
          </button>
          <button
            :class="{ 'show-state': !isPlaying }"
            class="main-state state-btn play-btn"
            aria-label="afspelen"
            @click="togglePlayPause"
          >
            <Icon name="eenvandaag-general:play-solid" role="presentation" />
          </button>

          <button
            ref="forwardState"
            class="state-btn state-forward"
            aria-label="5 seconden vooruit"
            @click="fastForward"
          >
            <span class="forward-duration">5</span>
            <Icon name="eenvandaag-general:forward" role="presentation" />
          </button>
        </div>

        <div ref="controls" class="controls" :class="{ 'show-controls': !isPlaying }">
          <div
            ref="duration"
            class="duration"
            @mousemove="onHoverTimeline"
            @mouseleave="onLeaveTimeline"
            @mousedown="onTimelineDragStart"
            @touchstart="onTimelineDragStart"
          >
            <div class="buffer" :style="{ width: bufferProgress + '%' }"></div>
            <div ref="currentTimeBar" class="current-time" :style="{ width: progress + '%' }"></div>

            <!-- The hover time box -->
            <div v-if="hoverTime !== ''" class="hover-time" :style="{ left: hoverPosition + '%' }">
              <span class="hover-duration">{{ hoverTime }}</span>
            </div>
          </div>

          <div class="btn-controls" tabindex="0">
            <div class="btn-con">
              <button
                tabindex="0"
                :aria-label="isPlaying ? 'pauze' : 'play'"
                class="play-pause control-btn"
                @click="togglePlayPause"
              >
                <Icon
                  :name="isPlaying ? 'eenvandaag-general:pause' : 'eenvandaag-general:play-solid'"
                  role="presentation"
                />
              </button>

              <div class="volume">
                <button
                  class="mute-unmute control-btn"
                  :aria-label="isMuted ? 'gedempt' : 'niet dempen'"
                  @click="toggleMute"
                >
                  <Icon
                    :name="isMuted ? 'eenvandaag-general:volume-mute' : 'eenvandaag-general:volume'"
                    role="presentation"
                  />
                </button>

                <div ref="totalVol" class="max-vol">
                  <input
                    v-model="volume"
                    type="range"
                    class="volume-slider"
                    min="0"
                    max="1"
                    step="0.01"
                    :style="{
                      background: `linear-gradient(to right, #ff6a00 ${volume * 100}%, #ccc ${volume * 100}%)`,
                    }"
                    @input="onVolumeChange"
                  />
                </div>
              </div>
              <div class="time-container">
                <div class="current-duration">{{ formattedCurrentTime }}</div>
                <div>/</div>
                <div class="total-duration">{{ totalTime }}</div>
              </div>
            </div>

            <div class="right-controls">
              <button class="backward control-btn" aria-label="5 seconden terug" @click="rewind">
                <Icon name="eenvandaag-general:rewind" role="presentation" />
              </button>
              <button
                class="forward control-btn"
                aria-label="5 seconden vooruit"
                @click="fastForward"
              >
                <Icon name="eenvandaag-general:forward" role="presentation" />
              </button>
              <button
                class="mini-player control-btn"
                :aria-label="isMiniPlayer ? 'Mini speler sluiten' : 'Mini speler tonen'"
                @click="toggleMiniPlayer"
              >
                <Icon
                  :name="
                    isMiniPlayer
                      ? 'eenvandaag-general:aspect-ratio'
                      : 'eenvandaag-general:mini-player'
                  "
                  role="presentation"
                />
              </button>
              <button
                class="fullscreen-btn control-btn"
                :aria-label="isFullscreen ? 'Sluit Volledig scherm' : 'Volledig scherm openen'"
                @click="toggleFullscreen"
              >
                <Icon
                  :name="
                    isFullscreen
                      ? 'eenvandaag-general:fullscreen-exit'
                      : 'eenvandaag-general:fullscreen'
                  "
                  role="presentation"
                />
              </button>
            </div>
          </div>
        </div>

        <span v-if="loading" class="video-loader"></span>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  src: {
    type: String,
    default: null,
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  poster: {
    type: String,
    default: null,
  },
  native: {
    type: Boolean,
    default: false,
  },
});

const observer = ref();

const video = ref<HTMLVideoElement | null>(null);
const controls = ref<HTMLDivElement | null>(null);
const duration = ref<HTMLDivElement | null>(null);
const currentTimeBar = ref<HTMLDivElement | null>(null);
const totalVol = ref<HTMLDivElement | null>(null);

const isPlaying = ref(false);
const isFullscreen = ref(false);
const isMiniPlayer = ref(false);
const loading = ref(true);
const isDragging = ref(false);

const isMuted = ref(props.autoplay);
const lastVolume = ref(1); // Variable to store the last volume before muting

const progress = ref(0); // Current progress in percentage
const bufferProgress = ref(0); // Buffer progress in percentage
const volume = ref(1); // Volume level from 0 to 1

const currentTime = ref(0); // Track the currentTime of the video
const totalTime = ref('00:00'); // Track the totalTime of the video

// State for hover-related functionality
const hoverTime = ref(''); // The formatted hover time
const hoverPosition = ref(0); // Position in percentage of the progress bar

const backwardState = ref<HTMLDivElement | null>(null);
const forwardState = ref<HTMLDivElement | null>(null);

onMounted(() => {
  nextTick(() => {
    if (!video.value) return;
    // Set initial volume and muted state
    initializeMutedAutoplay();

    video.value.volume = volume.value;
    totalTime.value = formatTime(video.value.duration);

    if (props.autoplay) {
      if (video.value.src) {
        video.value.play();
        isPlaying.value = true;
      }
    }

    // Manually check the `readyState` when mounted
    if (video.value.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      handleCanPlay(); // Trigger manually if the video is already playable
    }

    if (backwardState.value) {
      backwardState.value.addEventListener('animationend', () => {
        backwardState.value?.classList.remove('show-state');
        backwardState.value?.classList.remove('animate-state');
      });
    }

    if (forwardState.value) {
      forwardState.value.addEventListener('animationend', () => {
        forwardState.value?.classList.remove('show-state');
        forwardState.value?.classList.remove('animate-state');
      });
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    video.value.addEventListener('keydown', handleKeyboardShortcuts);
    video.value.addEventListener('ended', onVideoEnded);
    video.value.addEventListener('canplay', handleCanPlay);
  });

  createObserver();

  if (video.value) {
    observer.value.observe(video.value);
  }

  loading.value = false;
});

onUnmounted(() => {
  if (video.value) {
    observer.value.unobserve(video.value);
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
    video.value.removeEventListener('keydown', handleKeyboardShortcuts);

    video.value.removeEventListener('ended', onVideoEnded);
    video.value.removeEventListener('canplay', handleCanPlay);
  }
});

const disableContext = (event: MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

const createObserver = () => {
  observer.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (video.value) {
          if (entry.isIntersecting) {
            // Video is in view
          } else {
            // Video is out of view, pause playback
            video.value.pause();
            video.value.blur();
          }
        }
      });
    },
    {
      threshold: 0.5, // Adjust threshold as necessary
    },
  );
};

// Handle "canplay" and fallback behavior
const handleCanPlay = () => {
  loading.value = false;
  if (video.value) {
    video.value.volume = volume.value;
    totalTime.value = formatTime(video.value.duration || 0);
  }
};

/**
 * Ensure that autoplay muted state is synced properly
 */
const initializeMutedAutoplay = () => {
  if (video.value) {
    video.value.muted = props.autoplay; // If autoplay is enabled, force mute
    isMuted.value = video.value.muted; // Sync reactive state

    if (isMuted.value) {
      // Start with volume 0 if muted
      volume.value = 0;
    } else {
      // If not muted, use current video volume
      volume.value = video.value.volume || 1; // Default to 1
      lastVolume.value = volume.value; // Store the initial non-muted volume
    }
  }
};

const onVideoEnded = () => {
  if (video.value) {
    video.value.load();
    video.value.currentTime = 0;
    progress.value = 0;
    isPlaying.value = false;
  }
};

// Function triggered when mouse moves over the timeline
const onHoverTimeline = (event: MouseEvent) => {
  if (!video.value || !duration.value) return;

  const timelineRect = duration.value.getBoundingClientRect();

  const hoverX = Math.min(Math.max(event.clientX - timelineRect.left, 0), timelineRect.width);
  const hoverPercentage = hoverX / timelineRect.width;
  const hoverSeconds = hoverPercentage * video.value.duration;

  // Update hover state
  hoverTime.value = formatTime(hoverSeconds);
  hoverPosition.value = hoverPercentage * 100; // Hover position in percentage
};

// Function triggered when mouse leaves the timeline
const onLeaveTimeline = () => {
  hoverTime.value = '';
  hoverPosition.value = 0;
};

const onTimelineDragStart = (event: MouseEvent | TouchEvent) => {
  if (!video.value) return;

  isDragging.value = true;

  // Normalize event for both mouse and touch
  const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0]?.clientX;

  updateCurrentTime(clientX); // Update immediately on drag start

  // Add global event listeners for mousemove and mouseup
  document.addEventListener('mousemove', onTimelineDragging);
  document.addEventListener('touchmove', onTimelineDragging);
  document.addEventListener('mouseup', onTimelineDragEnd);
  document.addEventListener('touchend', onTimelineDragEnd);
};

// Function triggered on mousemove event (while dragging)
const onTimelineDragging = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return; // Ensure this only runs while dragging
  // Normalize event for both mouse and touch
  const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0]?.clientX;
  updateCurrentTime(clientX);
};

// Function triggered on mouseup event (end drag)
const onTimelineDragEnd = () => {
  isDragging.value = false;

  // Clean up the event listeners
  document.removeEventListener('mousemove', onTimelineDragging);
  document.removeEventListener('touchmove', onTimelineDragging);
  document.removeEventListener('mouseup', onTimelineDragEnd);
  document.removeEventListener('touchend', onTimelineDragEnd);
};

const togglePlayPause = () => {
  if (video.value) {
    video.value.focus();
    if (isPlaying.value) {
      video.value.pause();
      isPlaying.value = false;
    } else {
      video.value.play();
      isPlaying.value = true;
    }
  }
};

const onPlay = () => {
  isPlaying.value = true;
};

const onPause = () => {
  isPlaying.value = false;
};

const updateProgress = () => {
  if (video.value) {
    currentTime.value = video.value.currentTime; // Update reactive value
    progress.value = (video.value.currentTime / video.value.duration) * 100;
    updateBuffer(); // Ensure buffer updates
  }
};

const updateBuffer = () => {
  if (!video.value || video.value.buffered.length === 0) return;
  bufferProgress.value =
    (video.value.buffered.end(video.value.buffered.length - 1) / video.value.duration) * 100;
};

/**
 * Update video currentTime based on timeline interaction
 */
const updateCurrentTime = (clientX: number | undefined) => {
  if (!clientX) return;
  if (!video.value || !duration.value) return;

  const timelineRect = duration.value.getBoundingClientRect();
  const clickPosition = Math.min(Math.max(clientX - timelineRect.left, 0), timelineRect.width);
  const newTime = (clickPosition / timelineRect.width) * video.value.duration;

  // Update video time
  video.value.currentTime = newTime;

  // Update visible progress bar
  progress.value = (newTime / video.value.duration) * 100;
};

/**
 * Computed for currentTime
 */
const formattedCurrentTime = computed(() => formatTime(currentTime.value));

/**
 * Function to format seconds to MM:SS
 */
const formatTime = (seconds: number): string => {
  if (isNaN(seconds)) return '00:00'; // Handle NaN values gracefully
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

/**
 * Show Player in Fullscreen
 */
const toggleFullscreen = () => {
  if (!video.value) return;

  if (!isFullscreen.value) {
    video.value.requestFullscreen();
    isFullscreen.value = true;
  } else {
    document.exitFullscreen();
    isFullscreen.value = false;
  }
};

const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement;
};

/**
 * Show player as Floating Mini Player
 */
const toggleMiniPlayer = () => {
  if (document.pictureInPictureElement) {
    isMiniPlayer.value = false;
    document.exitPictureInPicture();
  } else {
    video.value?.requestPictureInPicture();
    isMiniPlayer.value = true;
  }
};

/**
 * Handle volume slider input and toggle mute
 */
const onVolumeChange = () => {
  if (video.value) {
    // Update video element's volume
    video.value.volume = volume.value;

    if (volume.value > 0) {
      // If volume is set to > 0, unmute and save current volume
      video.value.muted = false;
      isMuted.value = false;
      lastVolume.value = volume.value; // Store the last non-zero volume
    } else {
      // Mute the video if volume is 0
      video.value.muted = true;
      isMuted.value = true;
    }
  }
};

/**
 * Toggle the mute state and restore last volume
 */
const toggleMute = () => {
  if (video.value) {
    if (isMuted.value) {
      // If currently muted, unmute and restore volume
      video.value.muted = false;
      isMuted.value = false;

      // Restore the last volume (or default to 1 if not set)
      volume.value = lastVolume.value > 0 ? lastVolume.value : 1;
      video.value.volume = volume.value;
    } else {
      // If not muted, mute the video
      video.value.muted = true;
      isMuted.value = true;

      // Save the current volume before muting
      lastVolume.value = volume.value;
      volume.value = 0; // Set slider to 0 when muted
    }
  }
};

/**
 * Handle Rewind and FastForward
 */
const rewind = () => {
  if (backwardState.value) {
    backwardState.value.classList.add('show-state');
    backwardState.value.classList.add('animate-state');
  }
  if (video.value) {
    video.value.currentTime = Math.max(video.value.currentTime - 5, 0);
  }
};

const fastForward = () => {
  if (forwardState.value) {
    forwardState.value.classList.add('show-state');
    forwardState.value.classList.add('animate-state');
  }
  if (video.value) {
    video.value.currentTime = Math.min(video.value.currentTime + 5, video.value.duration);
  }
};

/**
 * Handle Mouse Movement
 */
const onMouseMove = () => {
  if (controls.value) {
    controls.value.classList.add('show-controls');
  }
};

const onMouseLeave = () => {
  hideControlsWithDelay();
};

/**
 * Hide Controls after delay
 */
const hideControlsWithDelay = () => {
  setTimeout(() => {
    if (controls.value) controls.value.classList.remove('show-controls');
  }, 2000);
};

/**
 * HandleKeyboard key events
 */
const handleKeyboardShortcuts = (event: KeyboardEvent) => {
  event.stopPropagation();
  event.preventDefault();
  const tagName = document.activeElement?.tagName.toLowerCase();

  if (tagName === 'input') return;
  if (!video.value) return;

  switch (event.key) {
    case ' ':
      togglePlayPause();
      break;
    case 'ArrowRight':
      fastForward();
      break;
    case 'ArrowLeft':
      rewind();
      break;
    case 'f':
      toggleFullscreen();
      break;
    case 'm':
      toggleMute();
      break;
    default:
      break;
  }
};
</script>

<style scoped lang="postcss">
.video-container {
  --video-aspect-ratio: 16 / 9;
  --video-background-color: hsl(var(--color-primary-900));
  --video-width: 100%;
  --video-max-width: 100%;
  --video-max-height: 100%;
  --video-primary-color: hsl(var(--color-brand-light));

  width: 100%;
  aspect-ratio: var(--video-aspect-ratio);
  position: relative;
  overflow: hidden;
  background: var(--video-background-color);
  display: flex;
  align-items: center;
  justify-content: center;

  video {
    width: var(--video-width);
    max-width: var(--video-max-width);
    max-height: var(--video-max-height);
  }
}

.video-loader {
  --video-loader-height: var(--unit-48);
  --video-loader-width: var(--unit-48);
  --video-loader-color: var(--video-primary-color);
  --video-loader-border-width: var(--unit-4);

  position: absolute;
  width: var(--video-loader-width);
  height: var(--video-loader-height);
  border-radius: 50%;
  border-top: var(--video-loader-border-width) solid transparent;
  border-right: var(--video-loader-border-width) solid var(--video-loader-color);
  border-left: var(--video-loader-border-width) solid var(--video-loader-color);
  border-bottom: var(--video-loader-border-width) solid var(--video-loader-color);
  z-index: 9999;
  animation: rotation 2s infinite linear;
  -webkit-animation: rotation 2s infinite linear;
  display: block;
}

.player-state {
  display: flex;
  position: absolute;
  width: 100%;
  justify-content: space-around;
  pointer-events: none;
}

.state-btn {
  --state-btn-size: calc(var(--unit-40) * 2);
  --state-btn-background-color: hsla(var(--color-primary-950), 1);
  --state-btn-color: hsla(var(--color-neutral-white), 1);

  font-size: var(--unit-24);
  width: var(--state-btn-size);
  height: var(--state-btn-size);
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  background: var(--state-btn-background-color);
  z-index: 9999;
  -webkit-tap-highlight-color: transparent;
  opacity: 0;
  user-select: none;
  transform: scale(0);
  pointer-events: none;
  color: var(--state-btn-color);
  border: unset;

  @add-mixin hover {
    scale: 1.1;
  }
}

.play-btn {
  --play-btn-width: 70px;
  --play-btn-height: 48px;
  width: var(--play-btn-width);
  height: var(--play-btn-height);
  border-radius: var(--unit-4);
  background: var(--effect-gradient-brand);
  box-shadow: 0 -1px 0 0 rgba(14, 22, 46, 0.1) inset;
  font-size: var(--unit-24);

  @add-mixin hover {
    scale: 1.1;
  }

  @media (--md-viewport) {
    --play-btn-width: 84px;
    --play-btn-height: 58px;
  }
}

.state-forward,
.state-backward {
  font-size: var(--unit-24);

  .forward-duration {
    margin-right: var(--unit-4);
  }
}

.show-state {
  transform: scale(1);
  opacity: 1;
  pointer-events: all;
}

.animate-state {
  animation: playPause 0.5s forwards;
}

.show-controls {
  opacity: 1 !important;
  transform: translateY(0) !important;
  visibility: visible !important;
}

.controls {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 5rem 1rem 0.5rem 1rem;
  width: 100%;
  background: linear-gradient(to top, hsl(var(--color-primary-950)) -100%, transparent);
  box-sizing: border-box;
  opacity: 0;
  transform: translateY(40px);
  visibility: hidden;
  z-index: 99;
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.duration {
  --duration-height: 8px;

  position: relative;
  width: 100%;
  height: var(--duration-height);
  border-radius: 4px;
  background: hsla(var(--color-primary-100), 0.2);
  cursor: pointer;
  transition: all 0.2s;

  .buffer {
    height: 100%;
    position: absolute;
    inset: 0;
    background-color: #ff6a0045;
    border-radius: 4px;
    z-index: 9;
    width: 0;
  }

  .current-time {
    height: 100%;
    position: absolute;
    inset: 0;
    border-radius: var(--unit-4);
    background: hsl(var(--color-brand-light));
    z-index: 999;
    display: flex;
    align-items: center;
    width: 0;

    &::before {
      content: '';
      position: absolute;
      right: calc((-25px / 2));
      background: var(--video-primary-color);
      width: 24px;
      height: 24px;
      border-radius: 50%;
      transition: all 0.2s;
      visibility: hidden;
      transform: scale(0);
      border: 1px solid #ffffff;
    }
  }

  @media (--md-viewport) {
    --duration-height: 10px;
  }

  @add-mixin hover {
    --duration-height: 12px;

    .hover-duration {
      visibility: visible;
      opacity: 1;
      transition: all 250ms;
      transform: scale(1);
    }

    .current-time::before {
      visibility: visible;
      transform: scale(1);
    }
  }
}

.hover-time {
  height: 100%;
  position: absolute;
  inset: 0;
  z-index: 99;
  display: flex;
  align-items: center;
  width: 0;
  translate: 0 -100%;
  @mixin label-xs {
  }

  .hover-duration {
    position: absolute;
    right: calc((-44px / 2));
    top: -25px;
    background: hsl(var(--color-primary-950));
    padding: var(--unit-4) var(--unit-8);
    border-radius: var(--unit-4);
    color: hsl(var(--color-neutral-white));
    visibility: hidden;
    opacity: 0;
    transform: scale(0);
  }
}

.btn-controls {
  padding-top: var(--unit-8);
  font-size: var(--unit-16);
  display: flex;
  align-items: center;
  justify-content: space-between;

  .backward,
  .forward {
    font-size: var(--unit-12);
  }
}

.btn-con {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.play-pause {
  display: flex;
  margin-right: 0.5rem;
}

.control-btn {
  --control-btn-size: var(--unit-36);

  width: var(--control-btn-size);
  height: var(--control-btn-size);
  font-size: var(--unit-16);
  border-radius: var(--unit-4);
  padding: 0;
  background: hsl(var(--color-primary-950));
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  box-sizing: border-box;
  position: relative;
  margin-right: var(--unit-8);
  transition: all 250ms ease;
  color: hsl(var(--color-neutral-white));
  cursor: pointer;

  &:last-child {
    margin-right: 0;
  }

  @add-mixin hover {
    border: 1px solid var(--video-primary-color);
    color: var(--video-primary-color);
  }
}

.time-container {
  padding-left: var(--unit-8);
  display: flex;
  color: hsl(var(--color-neutral-white));

  @add-mixin label-sm {
  }
}

.volume {
  display: flex;
  align-items: center;
  cursor: default;

  .mute-unmute {
    display: flex;
    cursor: pointer;
  }

  .max-vol {
    height: 8px;
    cursor: pointer;
    transition: all 0.1s;
    width: 0;
    visibility: hidden;
    transform: scaleX(0);
    transform-origin: left;
    display: flex;
    align-items: center;
  }

  .volume-slider {
    width: 100%;
    -webkit-appearance: none; /* Removes default browser styles */
    appearance: none;
    height: var(--unit-4);
    border-radius: var(--unit-4);
    outline: none;
    transition: background 250ms ease-in-out;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: var(--unit-12);
      height: var(--unit-12);
      border-radius: 50%;
      background: hsl(var(--color-neutral-white));
      cursor: pointer;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      transition: background 200ms ease-in-out;
    }

    &::-webkit-slider-thumb:hover {
      background: var(--video-primary-color);
    }
  }

  @add-mixin hover {
    .max-vol {
      width: 56px;
      visibility: visible;
      transform: scaleX(1);
    }
  }
}

.mini-player {
  display: none;

  @media (--md-viewport) {
    display: flex;
  }
}

.right-controls {
  display: flex;
  align-items: center;
}

.right-controls span {
  cursor: pointer;
}

@keyframes playPause {
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 0;
    transform: scale(1);
  }
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
